import { Grid, Typography } from '@mui/material';
import Button from 'components/Button';
import MultiPageBuilder from 'components/MultiPageBuilder';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import FullHeightView from 'components/new/FullHeightView';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CommitmentType } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import { numberToCurrencyString } from 'further-ui/utils';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CapitalActionFormValues } from '../hooks/useCapitalActionForm';
import useGetEntryTitle from '../hooks/useGetEntryTitle';
import InvestorBreakdown from './InvestorBreakdown';
import TransactionsReview from './TransactionsReview';
import useReviewDetails from './hooks/useReviewDetails';

type Props = {
  onPrevious: () => void;
  onSubmit: () => void;
  isLoading: boolean;
};

const CapitalActionReview: React.FC<Props> = ({
  onPrevious,
  onSubmit,
  isLoading,
}) => {
  const { id } = useParams<{ id?: string }>();
  const getEntryTitle = useGetEntryTitle();
  const { vehicle } = useLayoutContext();
  const { hasEqualisationInDraftState } = useEqualisations(vehicle?._id);
  const confirmDialog = useDisclosure();

  const { getValues } = useFormContext<CapitalActionFormValues>();
  const entries = getValues('entries');

  const { investorsBreakdown, totalAmount } = useReviewDetails({
    capitalActionId: id,
    entries,
    vehicleId: getValues('vehicleId'),
    date: getValues('ledgerDate'),
  });

  return (
    <FullHeightView
      actionBar={
        <Row spacing="sm" centered justify="flex-end">
          <Button
            variant="outlined"
            onClick={onPrevious}
            disabled={isLoading}
            size="large"
          >
            Previous step
          </Button>
          <Button
            variant="contained"
            onClick={
              hasEqualisationInDraftState ? confirmDialog.onOpen : onSubmit
            }
            loading={isLoading}
            size="large"
          >
            Save and approve
          </Button>
        </Row>
      }
    >
      <Grid container>
        <Grid size={{ xs: 12, lg: 11 }}>
          <Column spacing="md">
            <Typography variant="h4">
              Action overall investor breakdown
            </Typography>
            <InvestorBreakdown
              isCapitalAndLoan={
                vehicle?.commitmentType === CommitmentType.CapitalAndLoan
              }
              investorsBreakdown={investorsBreakdown}
              baseCurrency={vehicle?.baseCurrency}
            />
            <MultiPageBuilder
              summaryHeader="Capital action summary"
              summaryFooterTitle="Total to be called/(distributed)"
              summaryFooterTotals={numberToCurrencyString(totalAmount, {
                currency: vehicle?.baseCurrency,
                negativeInBrackets: true,
              })}
              defaultActivePageIndex={0}
              showSummaryOnly
            >
              {entries.map((entry, index) => (
                <MultiPageBuilder.Page
                  key={`entry-${index}`}
                  title={getEntryTitle(entry)}
                  totals={numberToCurrencyString(entry.totalAmount, {
                    currency: vehicle?.baseCurrency,
                  })}
                />
              ))}
            </MultiPageBuilder>
            <Typography variant="h4">Review accounts transactions</Typography>
            <TransactionsReview vehicleId={vehicle?._id ?? ''} />
          </Column>
        </Grid>
        {hasEqualisationInDraftState && (
          <PendingDraftEqualisationsModal
            isOpen={confirmDialog.isOpen}
            onClose={confirmDialog.onClose}
            onConfirm={onSubmit}
            loading={isLoading}
          />
        )}
      </Grid>
    </FullHeightView>
  );
};

export default CapitalActionReview;
